import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus, Search, Edit, Trash2, Loader2, MoreHorizontal,
  Printer, Eye, Truck, Package, MapPin
} from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import LineItemsForm from '../../components/LineItemsForm';

import { generateDocumentHTML } from '../../lib/documentTemplates';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  dispatched: 'bg-blue-100 text-blue-700',
  in_transit: 'bg-amber-100 text-amber-700',
  delivered: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const DeliveryChallan = () => {
  const { currentCompany } = useAuth();
  const [challans, setChallans] = useState([]);
  const [clients, setClients] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [editingChallan, setEditingChallan] = useState(null);
  const [selectedChallan, setSelectedChallan] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    client_id: '',
    sales_order_id: '',
    items: [{ id: '1', product_id: '', description: '', quantity: 1, unit_price: 0, tax_rate: '0', tax_amount: 0, subtotal: 0, line_total: 0 }],
    delivery_date: '',
    delivery_address: '',
    vehicle_number: '',
    driver_name: '',
    driver_phone: '',
    notes: '',
    status: 'draft',
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [challansRes, clientsRes, ordersRes] = await Promise.all([
        api.get('/delivery-challans'),
        api.get('/clients'),
        api.get('/sales-orders')
      ]);
      setChallans(challansRes.data);
      setClients(clientsRes.data);
      setSalesOrders(ordersRes.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getClient = (id) => clients.find(c => c.id === id);

  const openForm = (challan = null) => {
    if (challan) {
      setEditingChallan(challan);
      setFormData({
        client_id: challan.client_id || '',
        sales_order_id: challan.sales_order_id || '',
        items: challan.items?.length > 0 ? challan.items : [{ id: '1', product_id: '', description: '', quantity: 1, unit_price: 0, tax_rate: '0', tax_amount: 0, subtotal: 0, line_total: 0 }],
        delivery_date: challan.delivery_date || '',
        delivery_address: challan.delivery_address || '',
        vehicle_number: challan.vehicle_number || '',
        driver_name: challan.driver_name || '',
        driver_phone: challan.driver_phone || '',
        notes: challan.notes || '',
        status: challan.status || 'draft',
      });
    } else {
      setEditingChallan(null);
      setFormData({
        client_id: '', sales_order_id: '',
        items: [{ id: '1', product_id: '', description: '', quantity: 1, unit_price: 0, tax_rate: '0', tax_amount: 0, subtotal: 0, line_total: 0 }],
        delivery_date: '', delivery_address: '', vehicle_number: '', driver_name: '', driver_phone: '', notes: '', status: 'draft',
      });
    }
    setIsFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    setSubmitting(true);
    try {
      if (editingChallan) {
        await api.put(`/delivery-challans/${editingChallan.id}`, formData);
        toast.success('Delivery Challan updated');
      } else {
        await api.post('/delivery-challans', formData);
        toast.success('Delivery Challan created');
      }
      setIsFormOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this delivery challan?')) {
      try {
        await api.delete(`/delivery-challans/${id}`);
        toast.success('Deleted');
        fetchData();
      } catch (error) {
        toast.error('Failed');
      }
    }
  };

  const openPreview = (challan) => {
    setSelectedChallan(challan);
    const client = getClient(challan.client_id);
    const challanDoc = {
      ...challan,
      client_name: client?.name || 'Client',
      client_email: client?.email || '',
      client_address: challan.delivery_address || client?.address || '',
      total: challan.items?.reduce((sum, item) => sum + (item.quantity || 0), 0),
    };
    let html = generateDocumentHTML('classic', 'delivery_challan', challanDoc, currentCompany);
    html = html.replace('SALES ORDER', 'DELIVERY CHALLAN');
    setPreviewHtml(html);
    setIsPreviewOpen(true);
  };

  const handlePrint = (challan) => {
    const client = getClient(challan.client_id);
    const challanDoc = { ...challan, client_name: client?.name || '' };
    let html = generateDocumentHTML('classic', 'delivery_challan', challanDoc, currentCompany);
    html = html.replace('SALES ORDER', 'DELIVERY CHALLAN');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  // Auto-fill from sales order
  const handleSalesOrderChange = (orderId) => {
    const order = salesOrders.find(o => o.id === orderId);
    if (order) {
      setFormData({
        ...formData,
        sales_order_id: orderId,
        client_id: order.client_id,
        items: order.items || formData.items,
        delivery_address: getClient(order.client_id)?.address || '',
      });
    }
  };

  const filteredChallans = challans.filter(c =>
    c.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getClient(c.client_id)?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-teal-600" /></div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Delivery Challan</h1>
          <p className="text-slate-500 mt-1">Create and manage delivery notes for shipments</p>
        </div>
        <Button onClick={() => openForm()} className="bg-teal-700 hover:bg-teal-800">
          <Plus className="h-4 w-4 mr-2" />Create Challan
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-slate-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div><p className="text-sm text-slate-500">Draft</p><p className="text-2xl font-bold">{challans.filter(c => c.status === 'draft').length}</p></div>
            <Package className="h-8 w-8 text-slate-400" />
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div><p className="text-sm text-slate-500">Dispatched</p><p className="text-2xl font-bold">{challans.filter(c => c.status === 'dispatched').length}</p></div>
            <Truck className="h-8 w-8 text-blue-500" />
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div><p className="text-sm text-slate-500">In Transit</p><p className="text-2xl font-bold">{challans.filter(c => c.status === 'in_transit').length}</p></div>
            <MapPin className="h-8 w-8 text-amber-500" />
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div><p className="text-sm text-slate-500">Delivered</p><p className="text-2xl font-bold">{challans.filter(c => c.status === 'delivered').length}</p></div>
            <Package className="h-8 w-8 text-green-500" />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search challans..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Challan #</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Delivery Date</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredChallans.length > 0 ? (
                filteredChallans.map((challan) => (
                  <TableRow key={challan.id}>
                    <TableCell className="font-medium">#{challan.id.slice(0, 8).toUpperCase()}</TableCell>
                    <TableCell>{getClient(challan.client_id)?.name || '-'}</TableCell>
                    <TableCell><Badge variant="outline">{challan.items?.length || 0} items</Badge></TableCell>
                    <TableCell>{challan.delivery_date ? new Date(challan.delivery_date).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>{challan.vehicle_number || '-'}</TableCell>
                    <TableCell><Badge className={STATUS_COLORS[challan.status]}>{challan.status?.replace('_', ' ')}</Badge></TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openPreview(challan)}><Eye className="h-4 w-4 mr-2" />Preview</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handlePrint(challan)}><Printer className="h-4 w-4 mr-2" />Print</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openForm(challan)}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(challan.id)} className="text-red-600"><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={7} className="h-32 text-center text-slate-500"><Truck className="h-8 w-8 mx-auto mb-2 text-slate-300" />No challans found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingChallan ? 'Edit Delivery Challan' : 'Create Delivery Challan'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit}>
            {/* Sales Order Selection */}
            <div className="mb-4 p-4 bg-slate-50 rounded-lg">
              <Label className="text-sm font-medium">Create from Sales Order (Optional)</Label>
              <Select value={formData.sales_order_id} onValueChange={handleSalesOrderChange}>
                <SelectTrigger className="mt-2"><SelectValue placeholder="Select sales order" /></SelectTrigger>
                <SelectContent>
                  {salesOrders.map(order => (
                    <SelectItem key={order.id} value={order.id}>#{order.id.slice(0, 8)} - {getClient(order.client_id)?.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <LineItemsForm
              items={formData.items}
              onItemsChange={(items) => setFormData({ ...formData, items })}
              clients={clients}
              onClientsChange={setClients}
              selectedClientId={formData.client_id}
              onClientChange={(id) => setFormData({ ...formData, client_id: id })}
              discount={0}
              onDiscountChange={() => {}}
              discountType="fixed"
              onDiscountTypeChange={() => {}}
            />

            {/* Delivery Details */}
            <div className="mt-6 p-4 bg-slate-50 rounded-lg space-y-4">
              <h3 className="font-medium text-slate-900">Delivery Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>Delivery Date</Label>
                  <Input type="date" value={formData.delivery_date} onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })} />
                </div>
                <div className="grid gap-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(val) => setFormData({ ...formData, status: val })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="dispatched">Dispatched</SelectItem>
                      <SelectItem value="in_transit">In Transit</SelectItem>
                      <SelectItem value="delivered">Delivered</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Delivery Address</Label>
                <Textarea value={formData.delivery_address} onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })} rows={2} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="grid gap-2">
                  <Label>Vehicle Number</Label>
                  <Input value={formData.vehicle_number} onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })} placeholder="ABC-1234" />
                </div>
                <div className="grid gap-2">
                  <Label>Driver Name</Label>
                  <Input value={formData.driver_name} onChange={(e) => setFormData({ ...formData, driver_name: e.target.value })} />
                </div>
                <div className="grid gap-2">
                  <Label>Driver Phone</Label>
                  <Input value={formData.driver_phone} onChange={(e) => setFormData({ ...formData, driver_phone: e.target.value })} />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Notes</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={2} />
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-teal-700 hover:bg-teal-800" disabled={submitting}>
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editingChallan ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Delivery Challan Preview</DialogTitle></DialogHeader>
          
          <div className="border rounded-lg overflow-hidden mt-4" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPreviewOpen(false)}>Close</Button>
            <Button variant="outline" onClick={() => selectedChallan && handlePrint(selectedChallan)}><Printer className="h-4 w-4 mr-2" />Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryChallan;
